/** @type {any} */
const ShopifyAny = /** @type {any} */ (
  typeof Shopify !== "undefined" ? Shopify : undefined
);

/** @type {any} */
const ThemeAny = /** @type {any} */ (
  typeof Theme !== "undefined" ? Theme : undefined
);

import { DialogComponent } from "./dialog.js";
import { fetchConfig } from "./utilities-v2.js";
import { CartAddEvent, SellingPlanUpdateEvent } from "./events.js";
import { getCart, primeCartCache } from "./pyp-gift-manager.js";

/**
 * Added To Cart drawer component.
 * Opens when a one-time product is added via product form.
 * Fetches the real PDP page to get gr-product-options with theme editor settings.
 *
 * @extends {DialogComponent}
 */
class AddedToCartDrawer extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Document>} */
  #cachedPages = new Map();

  /** @type {Map<string, Promise<Document | null>>} */
  #pendingFetches = new Map();

  /** @type {string | null} */
  #currentVariantId = null;

  /** @type {string | null} */
  #selectedSellingPlanId = null;

  /** @type {any | null} */
  #lineItem = null;

  /** @type {string | null} */
  #lineItemKey = null;

  /** @type {number} */
  #addedQuantity = 1;

  /** @type {string | null} */
  #addedSellingPlanId = null;

  /** @type {number} */
  #originalLineItemQuantity = 0;

  /** @type {HTMLElement | null} */
  #optionsElement = null;

  /** @type {boolean} */
  #isHandling = false;

  /** @type {boolean} */
  #isMounting = false;

  /** @type {Promise<void>} */
  #cartUpdateQueue = Promise.resolve();

  /** @type {Error | null} */
  #cartUpdateError = null;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateText();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
    );
    this.#abortController?.abort();
  }

  #hydrateText() {
    const headingEl = /** @type {HTMLElement | null} */ (this.refs.headingText);

    if (headingEl) {
      headingEl.textContent = this.dataset.heading || "Added to Cart";
    }

    this.#updateFooter();
  }

  /**
   * Opens the cart drawer directly (used for nugget one-time purchases and gift boxes)
   */
  #openCartDrawer() {
    const cartDrawer = document.querySelector("gr-cart-drawer, cart-drawer");
    if (
      cartDrawer &&
      typeof (/** @type {any} */ (cartDrawer).open) === "function"
    ) {
      /** @type {any} */ (cartDrawer).open();
    }
  }

  /**
   * Fetches the product page content (with caching and deduplication)
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // Return cached version if available
    const cached = this.#cachedPages.get(productPageUrl);
    if (cached) return cached;

    // Return pending fetch if one is already in-flight
    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    // Create new fetch promise
    const fetchPromise = this.#doFetch(productPageUrl);
    this.#pendingFetches.set(productPageUrl, fetchPromise);

    try {
      const result = await fetchPromise;
      if (result) {
        this.#cachedPages.set(productPageUrl, result);
      }
      return result;
    } finally {
      this.#pendingFetches.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #doFetch(productPageUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, "text/html");
    } catch (error) {
      const err = /** @type {any} */ (error);
      if (err?.name === "AbortError") {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /** @param {CustomEvent} event */
  #handleCartAdd = async (event) => {
    const {
      source,
      sourceContext,
      didError,
      sellingPlanId,
      isAddon,
      variantId: dataVariantId,
      itemCount,
      cart: eventCart,
      addedItem,
    } = event.detail?.data || {};

    const variantId = dataVariantId || event.detail?.sourceId;

    if (source !== "product-form-component") return;
    if (sourceContext === "quick-view") return;
    if (didError) return;

    // Skip drawer for subscription adds UNLESS it's from a product card
    // Product card subscription adds should still open the drawer
    // PDP main form subscription adds go directly to gift selection
    const isFromProductCard = sourceContext === "product-card";
    if (sellingPlanId && !isFromProductCard) return;

    // Skip drawer for addon products (check event data)
    if (isAddon === true) return;

    // Also skip if we're on the addons collection page
    if (window.location.pathname.includes("/collections/pre-checkout-add-ons"))
      return;

    // Skip drawer for nugget one-time purchase - open cart drawer directly instead
    if (sourceContext === "nugget-onetime") {
      this.#openCartDrawer();
      return;
    }

    // Skip drawer for variant buy block one-time purchase - open cart drawer instead
    if (sourceContext === "variant-onetime") {
      this.#openCartDrawer();
      return;
    }

    if (!variantId) return;

    // Prevent duplicate handling
    if (this.#isHandling) return;
    this.#isHandling = true;

    // Capture the quantity and selling plan that was just added (from the form submission)
    this.#addedQuantity = itemCount || 1;
    // Store the selling plan ID from the form submission - this is the source of truth
    this.#addedSellingPlanId = sellingPlanId || null;

    try {
      // Pass the added item from /cart/add.js response directly
      // This avoids Shopify's eventual consistency issues with /cart.js
      await this.#processCartAdd(variantId, eventCart, addedItem);
    } finally {
      this.#isHandling = false;
    }
  };

  /**
   * Process the cart add event after validation
   * @param {string | number} variantId
   * @param {Object} [eventCart] - Cart data from the event (avoids stale cache)
   * @param {Object} [addedItem] - The item returned from /cart/add.js response
   */
  async #processCartAdd(variantId, eventCart, addedItem) {
    // Trust the /cart/add.js response when it carries a variant_id: that's
    // what actually landed in the cart. The form's `variantId` can drift from
    // the cart's variant when something (e.g. Intelligems) swaps it at the
    // network layer, so always sync currentVariantId off addedItem when we
    // have it. Falling back to the dispatched variantId only when the response
    // doesn't include one.
    const effectiveVariantId = addedItem?.variant_id
      ? String(addedItem.variant_id)
      : variantId.toString();
    this.#currentVariantId = effectiveVariantId;

    if (addedItem && addedItem.variant_id) {
      this.#lineItem = { ...addedItem, line: 1 };
      this.#lineItemKey = addedItem.key || null;
      this.#originalLineItemQuantity = addedItem.quantity || 1;
    } else {
      // Fallback to fetching from cart (for backwards compatibility)
      const lineItemLoaded = await this.#populateLineItem(
        this.#currentVariantId,
        eventCart,
      );
      if (!lineItemLoaded || !this.#lineItem) {
        console.warn("Failed to load line item, drawer will not open");
        return;
      }
    }

    // Check if the product is a gift box - open cart drawer instead
    const boxHandle = this.#lineItem.handle || "";
    const sku = this.#lineItem.sku || "";
    const isGiftBox = boxHandle.includes("gift-box") || sku.startsWith("GBOX-");
    if (isGiftBox) {
      this.#openCartDrawer();
      return;
    }

    // Check if the product is an addon by checking product_type
    const productType = this.#lineItem.product_type?.toLowerCase();
    if (productType === "addon") return;

    // Render product info from line item
    this.#renderProductInfo();

    // Fetch product page and mount options
    const handle = this.#lineItem.handle;
    if (handle) {
      const html = await this.fetchProductPage(`/products/${handle}`);
      if (!html) {
        console.warn("Failed to fetch product page, drawer will not open");
        return;
      }

      // Check if the fetched page shows this is an addon
      if (this.#checkIfAddon(html)) return;

      await this.#mountProductOptions(html);
    }

    this.showDialog();
  }

  /**
   * @param {string} variantId
   * @param {Object} [eventCart] - Cart data from the event (avoids stale cache)
   * @returns {Promise<boolean>} Returns true if line item was successfully populated
   */
  async #populateLineItem(variantId, eventCart) {
    try {
      // Use cart from event if available (freshest data), otherwise fetch
      // This avoids race conditions with the cart cache deduplication window
      let cart = eventCart?.items ? eventCart : null;
      if (!cart) {
        cart = await getCart({ force: true });
      }
      if (cart) primeCartCache(cart);

      // Find all items matching this variant that are not PYP gifts or promo
      // free gifts (ACQ-299) — targeting a reconciler-owned gift line here
      // would let the subscription upsell convert a free line into a paid one.
      const matchingItems =
        cart?.items?.filter(
          /** @param {any} item */
          (item) =>
            String(item.variant_id) === String(variantId) &&
            !item.properties?._is_pyp_gift &&
            !item.properties?._free_gift,
        ) || [];

      // Get the LAST matching item (most recently added)
      // Items are ordered by time added, so last = most recent
      const match =
        matchingItems.length > 0
          ? matchingItems[matchingItems.length - 1]
          : null;

      if (match && cart?.items) {
        this.#lineItem = { ...match, line: cart.items.indexOf(match) + 1 };
        // Store the unique key for precise targeting in cart updates
        this.#lineItemKey = match.key || null;
        // Store the total quantity in cart for this line item
        this.#originalLineItemQuantity = match.quantity || 1;
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load cart item:", error);
      return false;
    }
  }

  #renderProductInfo() {
    const item = this.#lineItem;
    if (!item) return;

    const imageEl = /** @type {HTMLImageElement | null} */ (
      this.refs.productImage
    );
    const titleEl = /** @type {HTMLElement | null} */ (this.refs.productTitle);
    const variantEl = /** @type {HTMLElement | null} */ (
      this.refs.variantTitle
    );
    const linkEl = /** @type {HTMLAnchorElement | null} */ (
      this.refs.productLink
    );

    if (imageEl && item.image) {
      imageEl.src = item.image;
      imageEl.alt = item.product_title || "Product image";
    }

    if (titleEl) {
      titleEl.textContent = item.product_title || "";
    }

    if (variantEl) {
      const variantTitle =
        item.variant_title && item.variant_title !== "Default Title"
          ? item.variant_title
          : "";
      const priceText =
        item.price != null ? this.#formatMoney(item.price, true) : "";

      if (variantTitle && priceText) {
        variantEl.textContent = `${variantTitle} | ${priceText}`;
      } else if (variantTitle) {
        variantEl.textContent = variantTitle;
      } else {
        variantEl.textContent = priceText;
      }
    }

    if (linkEl) {
      linkEl.href = item.url || "#";
    }
  }

  /**
   * @param {Document} html
   */
  /**
   * Check if the product page indicates this is an addon product
   * @param {Document} html
   * @returns {boolean}
   */
  #checkIfAddon(html) {
    // Check for data-is-addon attribute on product-form-component
    const productFormComponent = html.querySelector(
      "product-form-component[data-is-addon]",
    );
    if (productFormComponent) return true;

    // Check for data attribute on other form elements
    const productForm = html.querySelector('[data-is-addon="true"]');
    if (productForm) return true;

    return false;
  }

  /**
   * Update option styling classes based on selection state
   * @param {Element | null} el
   * @param {boolean} isSelected
   */
  #setOptionSelected(el, isSelected) {
    if (!el) return;
    el.classList.toggle("border", !isSelected);
    el.classList.toggle("border-[#D8CFBB]", !isSelected);
    el.classList.toggle("bg-white", !isSelected);
    el.classList.toggle("border-[2.5px]", isSelected);
    el.classList.toggle("border-black-warm", isSelected);
    el.classList.toggle("bg-[#BDB0934D]", isSelected);
  }

  /**
   * Apply subscription state to the cloned options element
   * @param {HTMLElement} clone
   * @param {string | null} sellingPlanId
   */
  #applySubscriptionState(clone, sellingPlanId) {
    const subscriptionOption = clone.querySelector(
      '[ref="subscriptionOption"]',
    );
    const oneTimeOption = clone.querySelector('[ref="oneTimeOption"]');
    const subscriptionIndicator = clone.querySelector(
      '[ref="subscriptionIndicator"]',
    );
    const oneTimeIndicator = clone.querySelector('[ref="oneTimeIndicator"]');

    subscriptionOption?.setAttribute("data-selected", "true");
    oneTimeOption?.setAttribute("data-selected", "false");
    this.#setOptionSelected(subscriptionOption, true);
    this.#setOptionSelected(oneTimeOption, false);
    subscriptionIndicator?.classList.remove("hidden");
    oneTimeIndicator?.classList.add("hidden");

    // Set the frequency dropdown to match the selling plan
    const freqSelect = clone.querySelector('[ref="frequencySelect"]');
    if (freqSelect instanceof HTMLSelectElement && sellingPlanId) {
      for (let i = 0; i < freqSelect.options.length; i++) {
        const option = freqSelect.options[i];
        if (option?.dataset?.sellingPlanId === sellingPlanId) {
          freqSelect.selectedIndex = i;
          break;
        }
      }
    }

    this.#selectedSellingPlanId = sellingPlanId;
  }

  /**
   * Apply one-time purchase state to the cloned options element
   * @param {HTMLElement} clone
   */
  #applyOneTimeState(clone) {
    const subscriptionOption = clone.querySelector(
      '[ref="subscriptionOption"]',
    );
    const oneTimeOption = clone.querySelector('[ref="oneTimeOption"]');
    const subscriptionIndicator = clone.querySelector(
      '[ref="subscriptionIndicator"]',
    );
    const oneTimeIndicator = clone.querySelector('[ref="oneTimeIndicator"]');

    subscriptionOption?.setAttribute("data-selected", "false");
    oneTimeOption?.setAttribute("data-selected", "true");
    this.#setOptionSelected(subscriptionOption, false);
    this.#setOptionSelected(oneTimeOption, true);
    subscriptionIndicator?.classList.add("hidden");
    oneTimeIndicator?.classList.remove("hidden");

    this.#selectedSellingPlanId = null;
  }

  /**
   * @param {Document} html
   */
  async #mountProductOptions(html) {
    const host = /** @type {HTMLElement | null} */ (this.refs.optionsHost);
    if (!host) return;

    // Set mounting flag to ignore events during initialization
    this.#isMounting = true;

    host.innerHTML = "";
    this.#optionsElement = null;

    const optionsEl = html.querySelector("gr-product-options");
    if (!optionsEl) return;

    const clone = /** @type {HTMLElement} */ (optionsEl.cloneNode(true));
    clone.id = "added-to-cart-product-options";
    this.#optionsElement = clone;

    // Use the selling plan ID from the form submission (the source of truth for what was added)
    // NOT the line item's selling_plan_allocation (which could be from a different item)
    const itemHasSubscription = Boolean(this.#addedSellingPlanId);
    const itemSellingPlanId = this.#addedSellingPlanId;

    // Set the correct DOM state BEFORE appending (before connectedCallback runs)
    // This ensures the component initializes with the correct state
    if (itemHasSubscription) {
      this.#applySubscriptionState(clone, itemSellingPlanId);
    } else {
      this.#applyOneTimeState(clone);
    }

    // Now append - connectedCallback will read the correct initial state from DOM
    host.appendChild(clone);

    // Wait for custom element to be defined and upgraded
    await customElements.whenDefined("gr-product-options");
    customElements.upgrade(clone);

    // Also upgrade any frequency modal inside
    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    // Give the component time to initialize
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Update footer to match the selection
    this.#updateFooter(itemHasSubscription);

    // Clear mounting flag - initialization complete
    this.#isMounting = false;
  }

  /**
   * @param {number | string} value
   * @param {boolean} [removeTrailingZeros=false] - Whether to remove trailing zeros (e.g., $10.00 -> $10)
   */
  #formatMoney(value, removeTrailingZeros = false) {
    const moneyFormat = ThemeAny?.moneyFormat || ShopifyAny?.money_format;
    let formatted;
    if (ShopifyAny?.formatMoney) {
      formatted = ShopifyAny.formatMoney(value, moneyFormat);
    } else {
      const numericValue =
        typeof value === "number" ? value / 100 : Number(value) / 100;
      formatted = `$${numericValue.toFixed(2)}`;
    }

    if (removeTrailingZeros) {
      formatted = formatted.replace(/\.0+$/, "");
    }

    return formatted;
  }

  /** @param {SellingPlanUpdateEvent} event */
  #handleSellingPlanUpdate = (event) => {
    if (!this.#optionsElement?.contains(/** @type {Node} */ (event.target)))
      return;

    if (this.#isMounting) return;

    const { sellingPlanId } = event.detail || {};
    this.#selectedSellingPlanId = sellingPlanId || null;
    this.#updateFooter(Boolean(sellingPlanId));

    // Chain the update to the queue.
    // This allows other methods to wait for this specific operation to complete.
    this.#cartUpdateQueue = this.#cartUpdateQueue
      .then(async () => {
        this.#cartUpdateError = null;

        if (this.#lineItem?.line) {
          await this.#applySellingPlanToLineItem(sellingPlanId || null);
        }
      })
      .catch((err) => {
        console.error("Background selling plan update failed:", err);
        this.#cartUpdateError = err;
      });
  };

  /**
   * @param {boolean} [isSubscription] - Whether subscription is selected. If undefined, uses #selectedSellingPlanId
   */
  #updateFooter(isSubscription) {
    const isSub =
      isSubscription !== undefined
        ? isSubscription
        : Boolean(this.#selectedSellingPlanId);
    const footerEl = /** @type {HTMLElement | null} */ (this.refs.footerText);
    const ctaEl = /** @type {HTMLElement | null} */ (this.refs.ctaText);

    if (footerEl) {
      footerEl.textContent = isSub
        ? this.dataset.nextSubscription || ""
        : this.dataset.nextOneTime || "";
    }

    if (ctaEl) {
      ctaEl.textContent = isSub
        ? this.dataset.ctaSubscription || ""
        : this.dataset.ctaOneTime || "";
    }
  }

  async handleProceed() {
    // Wait for any pending selling plan changes to finish.
    // This ensures this.#lineItemKey is fresh before we try to use it.
    await this.#cartUpdateQueue;

    if (this.#cartUpdateError) {
      console.error("Cart update failed:", this.#cartUpdateError);
      this.#cartUpdateError = null;
      return;
    }

    if (this.#selectedSellingPlanId) {
      await this.#upgradeToSubscription();
      return;
    }
    this.#goToAddons();
  }

  #goToAddons() {
    const addonsUrl =
      this.dataset.addonsUrl || "/collections/pre-checkout-add-ons";
    window.location.assign(addonsUrl);
  }

  /**
   * Apply selling plan to the line item without redirecting
   * @param {string | null} sellingPlanId - The selling plan ID to apply, or null to remove subscription
   * @returns {Promise<boolean>} - Returns true if successful
   */
  async #applySellingPlanToLineItem(sellingPlanId) {
    // Prefer using the unique key, fall back to line number
    if (!this.#lineItemKey && !this.#lineItem?.line) {
      return false;
    }

    if (!ThemeAny?.routes?.cart_change_url) {
      console.error("Theme.routes.cart_change_url is not available");
      return false;
    }

    try {
      // Use the unique item key (id) for precise targeting, fall back to line number
      /** @type {any} */
      const body = this.#lineItemKey
        ? { id: this.#lineItemKey, quantity: this.#lineItem?.quantity ?? 1 }
        : {
            line: this.#lineItem.line,
            quantity: this.#lineItem?.quantity ?? 1,
          };

      // Only add selling_plan if we have one, otherwise omit to remove subscription
      if (sellingPlanId) {
        body.selling_plan = sellingPlanId;
      } else {
        body.selling_plan = null;
      }

      const response = await fetch(
        ThemeAny.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify(body),
        }),
      );
      const result = await response.json();

      if (result?.items) {
        primeCartCache(result);

        // Update the line item reference using the stored key for precise matching
        // When selling plan changes, the key changes too, so we need to find by variant + selling plan
        const updatedItem = result.items.find(
          /** @param {any} item */
          (item) => {
            const matchesVariant =
              String(item.variant_id) === String(this.#currentVariantId);
            const isNotGift =
              !item.properties?._is_pyp_gift && !item.properties?._free_gift;
            // Match by selling plan status to find the correct item after update
            const matchesSellingPlan = sellingPlanId
              ? item.selling_plan_allocation?.selling_plan?.id?.toString() ===
                sellingPlanId?.toString()
              : !item.selling_plan_allocation;
            return matchesVariant && isNotGift && matchesSellingPlan;
          },
        );

        if (updatedItem) {
          this.#lineItem = {
            ...updatedItem,
            line: result.items.indexOf(updatedItem) + 1,
          };
          // Update the stored key since it changes when selling plan changes
          this.#lineItemKey = updatedItem.key || null;
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to apply selling plan:", error);
      return false;
    }
  }

  async #upgradeToSubscription() {
    // Prefer using the unique key, fall back to line number
    if (!this.#lineItemKey && !this.#lineItem?.line) {
      this.#goToAddons();
      return;
    }

    if (!ThemeAny?.routes?.cart_change_url) {
      console.error("Theme.routes.cart_change_url is not available");
      this.#goToAddons();
      return;
    }

    try {
      // Generate UUID for gift linking - this ensures parentUuid is always available
      const lineItemId = crypto.randomUUID();

      // Use the unique item key (id) for precise targeting, fall back to line number
      const updatePayload = this.#lineItemKey
        ? { id: this.#lineItemKey, quantity: this.#lineItem?.quantity ?? 1 }
        : {
            line: this.#lineItem.line,
            quantity: this.#lineItem?.quantity ?? 1,
          };

      const response = await fetch(
        ThemeAny.routes.cart_change_url,
        fetchConfig("json", {
          body: JSON.stringify({
            ...updatePayload,
            selling_plan: this.#selectedSellingPlanId,
            properties: {
              ...(this.#lineItem?.properties || {}),
              _line_item_id: lineItemId,
              _parent_product: "yes",
              _has_double_gwp: Theme?.features?.double_gwp_enabled
                ? "true"
                : "false",
            },
          }),
        }),
      );
      const result = await response.json();

      if (result?.items) {
        primeCartCache(result);
      }

      const updatedItem = result?.items?.find(
        /** @param {any} item */
        (item) =>
          String(item.variant_id) === String(this.#currentVariantId) &&
          item.selling_plan_allocation &&
          !item.properties?._is_pyp_gift,
      );

      this.closeDialog();

      const giftDrawer = /** @type {any} */ (
        document.querySelector("gift-selection-drawer")
      );
      if (giftDrawer && updatedItem) {
        giftDrawer.showSelectionMode?.({
          parentItemId: String(updatedItem.id),
          parentItemKey: updatedItem.key,
          // Use the UUID we just added - no need to rely on potentially stale cache
          parentUuid: updatedItem.properties?._line_item_id || lineItemId,
          parentSellingPlanId:
            updatedItem.selling_plan_allocation?.selling_plan?.id ||
            this.#selectedSellingPlanId ||
            undefined,
          parentQuantity: updatedItem.quantity,
        });
      } else {
        this.#goToAddons();
      }
    } catch (error) {
      console.error("Failed to update selling plan:", error);
      this.#goToAddons();
    }
  }
}

if (!customElements.get("added-to-cart-drawer")) {
  customElements.define("added-to-cart-drawer", AddedToCartDrawer);
}
